import Popup from "reactjs-popup";
import React, { useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { AssemblyStore, AssemblyViewModelFromAssemblyStore, PortalShadeOption, PSOutdoorColor, PSOutdoorControlSide, PSOutdoorHeaderMountType, PSOutdoorHeaderTypeSku, PSOutdoorShadeOptionsDict, PSOutdoorSideGuideMountTypes, PSOutdoorSideGuides } from "../Store/entities/assemblies/types";
import Button from "../Parts/Button";
import PowerShadesTypeahead from "../Parts/PowerShadesTypeahead";
import { useNewPSOutdoorShadeOptions } from "../Store/entities/assemblies/PSOutdoorHooks";
import store from "../Store";
import { updateAssembly, InitialAssemblyState, InitialShadeState, updateAssemblies } from "../Store/entities/assemblies";
import { useAssembliesByQuoteId, UseAssembly } from "../Store/entities/assemblies/hooks";

import { useQuoteById, useQuoteTypeNameByQuoteId } from "../Store/entities/quotes/hooks";
import FractionField from "./NewOptionFields/FractionField";
import InfoBox from "./ShadeForm/InfoBox";
import ImageToolTip from "./ImageTooltip";
import PriceErrors from "./PriceErrors";
import apiCalls from "../PowerShadesAPIFunctions";
import { usePSOutdoorFabrics } from "../Store/entities/hooks";
import { useUser } from "../Store/user/hooks";

type EditPSOutdoorShadeModalProps = {
	isOpen: boolean;
	onClose: () => void;
	quoteId: number;
	sequenceId: number;
	sequenceIds?: number[];
	massEdit?: boolean | undefined;
};
type EditPSOutdoorShadeModalBaseProps = {
	isOpen: boolean;
	onClose: () => void;
	quoteId: number;
	assembly: AssemblyStore;
	setAssembly: (assembly: AssemblyStore) => void;
	isNotAdd?: boolean;
	massEdit?: boolean | undefined;
};

type EditPSOutdoorShadeModalBaseInnerProps = {
	options: PSOutdoorShadeOptionsDict;
	quoteType: string;
	isNotAdd?: boolean;
	massEdit?: boolean | undefined;
	unlocked?: boolean;
	errorSets?: string[];
};

const EditPSOutdoorShadeModalInner = ({ options, quoteType, isNotAdd = true, unlocked = true }: EditPSOutdoorShadeModalBaseInnerProps) => {
	const {
		Width,
		Height,
		Color,
		HeaderMountType,
		Fabric,
		ControlSide,
		HeaderType,
		SideGuides,
		SideGuideMountTypes,
		RoomName,
		ShadeName,
		DirectionFacing,
		Floor
	} = options;

	const disabled = !unlocked;

	const groupHeader = (groupName: string) => (
		<Row className={`form-group-title text-center m-2 mb-3 text-green border-success border-bottom text-${quoteType}`}>
			<Col>
				{groupName}
			</Col>
		</Row>
	);

	const optionLabel = (option: PortalShadeOption<any>) => {
		const { label } = option;

		return (
			<Row className="text-center mb-1 px-3" xs={12}>
				{
					// Only show icon and tooltip with these labels
					option.valuePopup
						? <div className="d-flex justify-content-center position-relative z-2">
							<label className="w-100">
								{label}
							</label>
							<ImageToolTip option={option} label={label} />
						</div>
						: <label>{label}</label>
				}

			</Row>
		);
	};

	const optionFooter = (option: PortalShadeOption<any>) => {
		const { info } = option;

		return (
			<Row className="text-center mb-1" xs={12}>
				{
					info && info.length > 0 && (
						<InfoBox info={info} />
					)
				}
			</Row>
		);
	};

	const errorClassName = (option: PortalShadeOption<any>) => {
		const { error } = option;

		return error && error.length > 0 && isNotAdd ? "error" : "";
	};

	let zScoreNum = 1;

	const zScore = () => {
		return zScoreNum++;
	}

	return (
		<Row >
			<Col>
				<Row>
					<Col
						key="form-group-header"
						className={` no-bottom header `}
					>
						<Row className="p-2">
							{groupHeader("Header")}
							<Col xs={6} className=" text-center ">
								{optionLabel(HeaderType)}
								<PowerShadesTypeahead
									disabled={disabled}
									id="header-type"
									options={HeaderType.options ?? []}
									onChange={(selected) => {
										if (selected.length === 0) {
											HeaderType.clearValue();
											return;
										}
										const selectedObj = selected[0] as {
											value: PSOutdoorHeaderTypeSku;
										};

										HeaderType.setValue(selectedObj.value);
									}}
									selected={[HeaderType.getSelectedOption()]}
									className={`text-center ${zScore()} ${errorClassName(HeaderType)}`}
								/>

							</Col>
							<Col xs={6} className=" text-center">
								{optionLabel(HeaderMountType)}
								<PowerShadesTypeahead
									disabled={disabled}
									id="header-mount-type"
									options={HeaderMountType.options ?? []}
									onChange={(selected) => {
										if (selected.length === 0) {
											HeaderMountType.clearValue();
											return;
										}
										const selectedObj = selected[0] as {
											value: PSOutdoorHeaderMountType;
										};

										HeaderMountType.setValue(selectedObj.value);
									}}
									selected={[HeaderMountType.getSelectedOption()]}
									className={`text-center ${zScore()} ${errorClassName(HeaderMountType)}`}
								/>

							</Col>
						</Row>
					</Col>
				</Row>

				<Row>
					<Col
						key="form-group-sizes"
						className={` no-bottom header `}
					>
						<Row className="p-2">
							{groupHeader("Sizes")}
							<Col>
								{optionLabel(Width)}
								<FractionField
									key={`width-${Width.varName}`}
									disabled={disabled}
									onBlur={(value) => Width.setValue(value)}
									value={Width.getValue()}
									minValue={Width.minValue ?? 0}
									maxValue={Width.maxValue ?? 0}
									varName="width"
									extraClasses={` ${errorClassName(Width)}`}
								/>
							</Col>
							<Col>
								{optionLabel(Height)}
								<FractionField
									key={`height-${Height.varName}`}
									disabled={disabled}
									onBlur={(value) => Height.setValue(value)}
									value={Height.getValue()}
									minValue={Height.minValue ?? 0}
									maxValue={Height.maxValue ?? 0}
									varName="height"
									extraClasses={` ${errorClassName(Height)}`}
								/>
							</Col>
						</Row>
					</Col>
				</Row>
				<Row className="p-2">
					{groupHeader("Options")}
					<Col xs={6}>
						{optionLabel(Color)}
						<PowerShadesTypeahead
							disabled={disabled}
							id="color"
							options={Color.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									Color.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value: PSOutdoorColor;
								};
								Color.setValue(selectedObj.value);
							}}
							selected={[Color.getSelectedOption()]}
							className={`text-center ${zScore()} ${errorClassName(Color)}`}
						/>
					</Col>
					<Col xs={6}>
						{optionLabel(Fabric)}
						<PowerShadesTypeahead
							disabled={disabled}
							id="fabric"
							options={Fabric.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									Fabric.clearValue();
									return;
								}
								const selectedFabric = selected[0] as {
									value: string;
								};
								Fabric.setValue(selectedFabric.value);
							}}
							selected={[Fabric.getSelectedOption()]}
							className={`text-center ${zScore()} ${errorClassName(Fabric)}`}
						/>
					</Col>
				</Row>
				<Row className="p-2">
					<Col xs={6}>
						{optionLabel(ControlSide)}
						<PowerShadesTypeahead
							disabled={disabled}
							id="control-side"
							options={ControlSide.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									ControlSide.clearValue();
									return;
								}

								const selectedObj = selected[0] as {
									value: PSOutdoorControlSide;
								};

								ControlSide.setValue(selectedObj.value);
							}}
							selected={[ControlSide.getSelectedOption()]}
							className={`text-center z-2 ${errorClassName(ControlSide)}`}
						/>
					</Col>
				</Row>
				<Row className="p-2">
					<Col xs={6}>
						{optionLabel(SideGuides)}
						<PowerShadesTypeahead
							disabled={disabled}
							id="side-guides"
							options={SideGuides.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									SideGuides.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value: PSOutdoorSideGuides;
								};
								SideGuides.setValue(selectedObj.value);
							}}
							selected={[SideGuides.getSelectedOption()]}
							className={`text-center ${zScore()} ${errorClassName(SideGuides)}`}
						/>
					</Col>
					<Col xs={6}>
						{optionLabel(SideGuideMountTypes)}
						<PowerShadesTypeahead
							disabled={disabled}
							id="side-guide-mount-types"
							options={SideGuideMountTypes.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									SideGuideMountTypes.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value: PSOutdoorSideGuideMountTypes;
								};

								SideGuideMountTypes.setValue(selectedObj?.value);
							}}
							selected={[SideGuideMountTypes.getSelectedOption()]}
							className={`text-center ${zScore()} ${errorClassName(SideGuideMountTypes)}`}
						/>
						{optionFooter(SideGuideMountTypes)}
					</Col>
				</Row>
				<Row className="p-2">
					{groupHeader("Attributes")}
					<Col>
						{optionLabel(DirectionFacing)}
						<PowerShadesTypeahead
							id="direction-facing"
							clearButton={DirectionFacing.getSelectedOption().label !== ""}
							options={DirectionFacing.options ?? []}
							onChange={(selected) => {
								if (selected.length === 0) {
									DirectionFacing.clearValue();
									return;
								}
								const selectedObj = selected[0] as {
									value;
								};

								DirectionFacing.setValue(selectedObj.value);
							}}
							selected={[DirectionFacing.getSelectedOption()]}
							className={`text-center ${zScore()} ${errorClassName(DirectionFacing)}`}
						/>
					</Col>
					<Col>
						{optionLabel(Floor)}
						<input
							type="number"
							className={`rounded input-group p-2 ${errorClassName(Floor)}`}
							value={Floor.getValue() || ""}
							onChange={(e) => Floor.setValue(Number(e.target.value))}
						/>
					</Col>
				</Row>
				<Row className="p-2">
					<Col>
						{optionLabel(RoomName)}
						<input
							className={`rounded input-group p-2 ${errorClassName(RoomName)}`}
							disabled={disabled}
							type="text"
							value={RoomName.getValue() || ""}
							onChange={(e) => RoomName.setValue(e.target.value)}
						/>
					</Col>
					<Col xs={6}>
						{optionLabel(ShadeName)}
						<input
							className={`rounded input-group p-2 ${errorClassName(ShadeName)}`}
							disabled={disabled}
							type="text"
							value={ShadeName.getValue()}
							onChange={(e) => ShadeName.setValue(e.target.value)}
						/>
					</Col>
				</Row>
			</Col>
		</Row>
	);
};

const EditPSOutdoorShadeModalBase = ({ isOpen, onClose, assembly: incomingAssembly, setAssembly: setAssemblyIncoming, isNotAdd = true, massEdit }: EditPSOutdoorShadeModalBaseProps) => {
	const [assembly, setAssembly] = React.useState(incomingAssembly);
	const quoteId = assembly.quote_id ?? 0;

	const psOutdoorFabrics = usePSOutdoorFabrics();

	const user = useUser();
	const quote = useQuoteById(quoteId);

	const shadeOptions = useNewPSOutdoorShadeOptions({ ...InitialAssemblyState(), ...assembly }, setAssembly, { fabrics: psOutdoorFabrics, user, quote });

	const quoteType = useQuoteTypeNameByQuoteId(quoteId);

	useEffect(() => {
		setAssembly(incomingAssembly);
	}, [isOpen]);

	const submitShade = () => {
		setAssemblyIncoming(assembly);
		onClose();
	};

	const [msrp, setMsrp] = React.useState(assembly.msrp);
	const [errors, setErrors] = React.useState(assembly.error_messages);

	useEffect(() => {
		if (quoteId === 0 || !isOpen) return;
		apiCalls.calculateAssemblyPriceNew(AssemblyViewModelFromAssemblyStore(assembly), quoteId, "psoutdoor").then((res) => {
			setMsrp(res?.data?.msrp ?? 0);
			const errorMessage = res?.data?.errors;
			const errorMessageListAsOneString = errorMessage?.join(", ") ?? "";
			setErrors(errorMessageListAsOneString);
		});
	}, [assembly]);

	if (!isOpen) return null;

	return (
		<Popup open={isOpen} onClose={onClose} className="mobile-modal p-2 m-2">
			<Row>
				{
					isNotAdd
						? <h2>Edit Shade</h2>
						: <h2>Add Shade</h2>
				}
			</Row>
			<EditPSOutdoorShadeModalInner
				options={shadeOptions}
				quoteType={quoteType}
				isNotAdd={isNotAdd}
				massEdit={massEdit}
				unlocked={assembly.unlocked}
			/>
			{
				isNotAdd && (
					<PriceErrors quoteId={quoteId} msrp={msrp} error_messages={errors ?? ""} assembly={assembly} />
				)
			}
			<Row className=" justify-content-center w-100 mb-1">
				<Button
					className="col-lg-4"
					onClick={(assembly.unlocked || !isNotAdd) ? submitShade : onClose}
				>
					Done
				</Button>
			</Row>
			{
				(!assembly.unlocked && isNotAdd)
					? <Row className="justify-content-center w-100">
						<Button className="col-lg-4" onClick={() => setAssembly({ ...assembly, unlocked: true })}>
							<FontAwesomeIcon icon={faLock} />
						</Button>
					</Row>
					: null
			}
		</Popup>
	);
};

const EditPSOutdoorShadeModal = ({ isOpen, onClose, quoteId, sequenceId, sequenceIds }: EditPSOutdoorShadeModalProps) => {
	const assy = UseAssembly(quoteId, sequenceIds?.length ? sequenceIds[0] as number : sequenceId);

	const setAssembly = (assembly: AssemblyStore) => {
		sequenceIds
			? sequenceIds.map((id) => {
				const newAssembly: AssemblyStore = { ...assembly, sequence_id: id };
				return store.dispatch(updateAssembly({ quoteId, assembly: newAssembly, sequenceId: id }));
			})
			: store.dispatch(updateAssembly({ quoteId, assembly, sequenceId }));
	};
	if (!assy) return null;
	return (
		<EditPSOutdoorShadeModalBase
			isOpen={isOpen}
			onClose={onClose}
			quoteId={quoteId}
			assembly={assy}
			setAssembly={setAssembly}
		/>
	);
};

const MassEditPSOutdoorModal = ({ quoteId, isOpen, onClose }) => {
	const assembly = { ...InitialAssemblyState(), unlocked: true, shades: [InitialShadeState()] };
	let selectedAssemblies = useAssembliesByQuoteId(quoteId).filter((assy) => assy.selected);

	const setAssembly = (assy) => {
		Object.entries(assembly).forEach(([key, value]) => {
			if (key === "shades") {
				Object.entries(value[0]).forEach(([shadeKey, shadeVal]) => {
					if (shadeVal !== assy.shades[0][shadeKey]) {
						selectedAssemblies = selectedAssemblies.map((selectedAssy) => (
							{
								...selectedAssy, shades: selectedAssy.shades.map((selectedShade) => (
									{ ...selectedShade, [shadeKey]: assy.shades[0][shadeKey] }
								))
							}
						));
					}
				});
			}
			if (assy[key] !== value && key !== 'shades') {
				selectedAssemblies = selectedAssemblies.map((selectedAssy) => ({ ...selectedAssy, [key]: assy[key] }));
			}
		});

		store.dispatch(updateAssemblies({ quoteId, assemblies: selectedAssemblies }));
	};

	return <EditPSOutdoorShadeModalBase
		isOpen={isOpen}
		onClose={onClose}
		quoteId={quoteId}
		assembly={assembly}
		setAssembly={setAssembly}
		massEdit
	/>;
};

export default EditPSOutdoorShadeModal;

export {
	EditPSOutdoorShadeModalBase,
	EditPSOutdoorShadeModalInner,
	MassEditPSOutdoorModal
};
